import ContainerMd from "../../components/Layouts/ContainerMd";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { PlusIcon } from "@heroicons/react/solid";
import IntervalContainer from "./freetime-schedule/IntervalContainer";
import { ToastContext } from "../../App";
import moment, { Moment } from "moment-timezone";
import { toNormalizedTime } from "../../services/schdeuleTimeOptionsService";
import AppLogo from "../../components/AppLogo";
import { isValidMoment } from "../../helpers/date_helpers";
import ChangeLangButton from "../../components/language/ChangeLangButton";
import ApiDateParser from "../../helpers/parsers/apiDateParser";
import { Schedule } from "../../api/@types/scheduleTypes";
import { classNames } from "../../helpers";
import TimeZoneAlert from "../../components/alerts/timeZone";

const WEEK_DAYS = [
  "saturday",
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
];

type FreeTimeScheduleProps = {
  onRemoveSlot?: (slot: any) => void;
  onSaveSlots: (slot: any) => Promise<void>;
  scheduleSlot?: any;
  setScheduleSlot?: any;
  prevPage?: () => void;
  removedScheduleSlot?: any;
  isEdit?: boolean;
  showLogo?: boolean;
  submitButtonText?: string;
  showChangeLanguage?: boolean;
  showHeader?: boolean;
  stepInfo?: string;
  previousButtonText?: string;
  containerStyles?: string;
};

const DEFAULT_SLOT_START = null;
const DEFAULT_SLOT_END = null;
export default function FreeTimeSchedule(props: FreeTimeScheduleProps) {
  const {
    scheduleSlot,
    prevPage,
    removedScheduleSlot,
    onRemoveSlot,
    previousButtonText,
    isEdit = false,
    onSaveSlots,
    showLogo,
    showHeader,
    stepInfo,
    showChangeLanguage,
    containerStyles,
  } = props;
  const [canUpdate, setCanUpdate] = useState<boolean>(true);
  const toast = useContext(ToastContext);
  const LocalBasePath = "page.freeTime";
  const { t } = useTranslation();
  const { submitButtonText = t(`${LocalBasePath}.continue`) } = props;
  const [schedule, setSchedule] = useState<{
    [day: string]: {
      id: number;
      startTime: Moment | null;
      endTime: Moment | null;
    }[];
  }>({});
  const [isDaysChanged, setIsDaysChanged] = useState(true);

  useEffect(() => {
    if (!!scheduleSlot) {
      setSchedule(scheduleSlot);
    }
  }, [scheduleSlot]);

  const addDayToSchedule = (day: string) => {
    setSchedule({
      ...schedule,
      [day]: [
        { id: -1, startTime: DEFAULT_SLOT_START, endTime: DEFAULT_SLOT_END },
      ],
    });
  };

  const removeDayFromSchedule = (day: string) => {
    removeAllDaySlots(day);
    let newSchedule = { ...schedule };
    delete newSchedule[day];
    setSchedule(newSchedule);
    if (isEdit) setCanUpdate(false);
  };

  const addSlotToSchedule = (
    day: string,
    startTime: Moment | null,
    endTime: Moment | null
  ) => {
    let newSchedule = { ...schedule };
    newSchedule[day].push({ id: -1, startTime, endTime });
    setSchedule(newSchedule);
    if (isEdit) setCanUpdate(false);
  };

  const removeSlotFromSchedule = (day: string, index: number) => {
    let newSchedule = { ...schedule };
    let slot = newSchedule[day][index];
    if (!!onRemoveSlot && slot.id != -1) {
      onRemoveSlot(slot);
    }
    newSchedule[day].splice(index, 1);
    setSchedule(newSchedule);
    if (newSchedule[day].length === 0) {
      removeDayFromSchedule(day);
    }
  };

  const removeAllDaySlots = (day: string) => {
    schedule[day].forEach((slot) => {
      if (!!onRemoveSlot && slot.id != -1) {
        onRemoveSlot(slot);
      }
    });
  };

  const updateSlot = (
    slotIndex: number,
    day: string,
    startTime: Moment | null,
    endTime: Moment | null
  ) => {
    let newSchedule = { ...schedule };
    let slot = newSchedule[day][slotIndex];
    slot["startTime"] = startTime;
    slot["endTime"] = endTime;
    newSchedule[day].splice(slotIndex, 1, slot);
    setSchedule(newSchedule);
    if (isEdit) setCanUpdate(false);
  };

  const updateSlotStartTime = (
    slotIndex: number,
    day: string,
    startTime: string
  ) => {
    let currentEndTime = schedule[day][slotIndex]["endTime"];
    updateSlot(slotIndex, day, moment(startTime), currentEndTime);
  };

  const updateSlotEndTime = (
    slotIndex: number,
    day: string,
    endTime: string
  ) => {
    let currentStartTime = schedule[day][slotIndex]["startTime"];
    updateSlot(slotIndex, day, currentStartTime, moment(endTime));
  };

  function isValidSchedule(scheduleData: {
    [day: string]: { startTime: Moment | null; endTime: Moment | null }[];
  }): boolean {
    if (Object.keys(scheduleData).length === 0) return false;
    let hasEmptyDays = false;
    let hasEmptyIntervals = false;
    for (let day of Object.keys(scheduleData)) {
      hasEmptyDays = scheduleData[day].length === 0;
      hasEmptyIntervals = !scheduleData[day].every(
        (slot) => isValidMoment(slot.startTime) && isValidMoment(slot.endTime)
      );
      if (hasEmptyDays || hasEmptyIntervals) break;
    }
    return !hasEmptyDays && !hasEmptyIntervals;
  }
  const previousPage = () => {
    if (!!prevPage) {
      prevPage();
    }
  };
  const onPressSave = () => {
    if (isValidSchedule(schedule)) {
      saveSchedule();
    } else {
      toast?.error("invalidSchedule");
    }
  };

  const generateApiScheduleData = () => {
    let apiScheduleData: Schedule = {
      active: true,
      schedule_slots_attributes: [],
    };
    /**
     * The order of adding the removed slots before the newly added or modified slots is IMPORTANT
     * The backend was giving a time range conflict error if you removed a slot then made a slot that has intersection with the removed one
     * So, we send the removed ones first then add the new one to prevent validations on a removed slots
     */
    if (isEdit) {
      // Adding removed slots
      removedScheduleSlot.map((slot: any) => {
        slot["_destroy"] = true;
        apiScheduleData.schedule_slots_attributes!.push(slot);
      });
    }
    // Adding new and modified slots
    Object.keys(schedule).forEach((day) => {
      schedule[day].forEach((slot: any) => {
        let slotData = {
          id: slot.id > 0 ? slot.id : null,
          start_time: ApiDateParser.normalize(
            ApiDateParser.toApiDate(slot.startTime)
          ),
          end_time: ApiDateParser.normalize(
            ApiDateParser.toApiDate(slot.endTime)
          ),
          day_of_the_week: day,
        };
        apiScheduleData.schedule_slots_attributes!.push(slotData);
      });
    });
    return apiScheduleData;
  };

  const saveSchedule = async () => {
    if (!!generateApiScheduleData()) {
      onSaveSlots(generateApiScheduleData());
    }
  };

  const onAddSlot = (day: string) => {
    addSlotToSchedule(day, DEFAULT_SLOT_START, DEFAULT_SLOT_END);
  };

  const isDayHaveDates = (day: string) => {
    return !!schedule[day] && schedule[day].length > 0;
  };

  const handleSelectDay = (event: any, value: string) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      addDayToSchedule(value);
    } else {
      removeDayFromSchedule(value);
    }
  };

  // @description: Prevent the form from being submitted each time the state is updated and the form is rerendered
  const onFormSubmit = (e: any) => {
    e.preventDefault();
  };

  return (
    <div className={containerStyles}>
      <div className="flex flex-col items-center justify-between w-full md:flex-row">
        {!!showLogo && <AppLogo className="mb-2 w-fit md:mb-0" />}
        {!!showLogo && showChangeLanguage && (
          <ChangeLangButton dropDownLanguage={true} />
        )}
      </div>
      <form
        onSubmit={onFormSubmit}
        className="flex flex-col flex-1 h-full divide-y divide-gray-200 "
      >
        {showHeader && (
          <div className="mb-8">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              {t(`${LocalBasePath}.freeTime`)}
            </h2>
          </div>
        )}
        <div className="flex-grow pb-8 ">
          {!showHeader && (
            <div className="mt-8">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                {t(`${LocalBasePath}.freeTime`)}
              </h3>
            </div>
          )}
          <div className="mt-4">
            <TimeZoneAlert />
          </div>
          <div className="mt-8">
            {!!isDaysChanged &&
              !!schedule.toString() &&
              WEEK_DAYS.map((day: string, index: any) => {
                return (
                  <div
                    className="gap-6 py-4 border-b border-gray-200 md:flex"
                    key={day + index}
                  >
                    <div className="relative flex justify-between">
                      <div
                        className={classNames(
                          "flex items-start gap-3 ",
                          !schedule[day] ? "" : "w-14"
                        )}
                      >
                        <div className="flex items-center h-5">
                          <input
                            checked={isDayHaveDates(day)}
                            onChange={(e) => handleSelectDay(e, day)}
                            onClick={(e) => handleSelectDay(e, day)}
                            id={day}
                            aria-describedby="comments-description"
                            name={day}
                            type="checkbox"
                            className="w-4 h-4 border-gray-300 rounded focus:ring-brandblue-500 text-brandblue-600"
                          />
                        </div>
                        <div className="text-sm rtl:mr-3 ltr:ml-3">
                          <label
                            htmlFor={day}
                            className="font-medium text-gray-700"
                          >
                            {t(`weekdays.${day}`)} &nbsp;
                          </label>
                          {!schedule[day] && (
                            <span
                              id="comments-description"
                              className="text-gray-500"
                            >
                              {t(`${LocalBasePath}.unavailable`)}
                            </span>
                          )}
                        </div>
                      </div>
                      {!!schedule[day] && (
                        <div className="flex items-start justify-end md:hidden rtl:mr-3 ltr:ml-3">
                          <button
                            onClick={() => onAddSlot(day)}
                            className=""
                            type="button"
                          >
                            <PlusIcon className="w-5 h-5 text-gray-600" />
                          </button>
                        </div>
                      )}
                    </div>
                    {!!schedule[day] && (
                      <div className="flex w-full mt-4 md:mt-0">
                        <div className="flex flex-col items-center flex-1">
                          {schedule[day].map((slot: any, indx: any) => (
                            <div
                              className="w-full pb-4 max-w-7xl"
                              key={
                                slot["startTime"] + slot["endTime"] + day + indx
                              }
                            >
                              <IntervalContainer
                                startTime={slot["startTime"]?.format()}
                                endTime={slot["endTime"]?.format()}
                                setStartTime={(val: string) =>
                                  updateSlotStartTime(indx, day, val)
                                }
                                setEndTime={(val: string) =>
                                  updateSlotEndTime(indx, day, val)
                                }
                                onRemove={() =>
                                  removeSlotFromSchedule(day, indx)
                                }
                              />
                            </div>
                          ))}
                        </div>
                        <div className="items-start justify-end hidden pt-2 md:flex rtl:mr-3 ltr:ml-3">
                          <button
                            onClick={() => onAddSlot(day)}
                            className=""
                            data-cy="addMoreTimes"
                            type="button"
                          >
                            <PlusIcon className="w-5 h-5 text-gray-600" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>

        <div className="z-[999] py-5 sticky bottom-0 bg-white flex justify-between items-center flex-col-reverse sm:flex-row gap-4">
          <div>
            {previousButtonText && (
              <button
                type="button"
                onClick={() => previousPage()}
                className="flex justify-center flex-shrink-0 px-4 py-2 text-sm font-medium bg-transparent border rounded-md shadow-sm border-gray text-gray focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500 disabled:bg-gray-400"
              >
                {previousButtonText}
              </button>
            )}
          </div>
          <div className="flex flex-col items-center justify-end gap-4 md:flex-row">
            <div className="flex justify-center w-full text-sm font-medium text-gray-700 md:justify-end">
              {stepInfo}
            </div>
            <button
              type="submit"
              className={classNames(
                "flex justify-center flex-shrink-0 px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-brandblue-600 hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500 disabled:bg-gray-400",
                isEdit && canUpdate ? "cursor-not-allowed" : ""
              )}
              onClick={onPressSave}
              disabled={isEdit ? canUpdate : false}
            >
              {submitButtonText}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
